import React, { useEffect, useState } from 'react';
import { Group, Select } from '@mantine/core';
import i18n from '../../../../i18n';
import { getTableCatalogSource } from '../../../utils/tableCatalogRegistry';
import { readTable } from '../../../utils/webTableRawCache';
import { apiRowsOf } from '../../../utils/webTableMaterialize';
import { useSelectedItemProps } from '../../../puck/fields/useSelectedItemProps';

/**
 * « Ligne de début » / « Ligne de fin » d'un tableau branché — le
 * `SelectTableRows` du Proposal : mêmes bornes comptées à partir de 1, mêmes
 * refus (un début après la fin, une fin avant le début), et une borne remise
 * à l'extrémité de la table s'efface.
 *
 * Les lignes proposées sont celles de la table entière, pas celles que le bloc
 * affiche : il n'en porte que la plage déjà choisie.
 */
export default function TableRowsRangeField({ disabled, onChange }) {
  const { itemProps } = useSelectedItemProps();
  const {
    tableName, tableConfig = null, tableSource = null, rowsSelectionStart, rowsSelectionEnd,
  } = itemProps;
  const [total, setTotal] = useState(0);

  useEffect(() => {
    let cancelled = false;
    readTable(tableSource || getTableCatalogSource(), tableName, tableConfig)
      .then((table) => !cancelled && setTotal(apiRowsOf(table)?.length || 0))
      .catch(() => !cancelled && setTotal(0));
    return () => { cancelled = true; };
  }, [tableName, tableConfig, tableSource]);

  if (!total) return null;

  const start = rowsSelectionStart || 1;
  const end = rowsSelectionEnd || total;
  const options = Array.from({ length: total }, (_, i) => String(i + 1));

  const selectStart = (picked) => {
    const next = Number(picked);
    if (next > end) return;
    onChange({ rowsSelectionStart: next === 1 ? null : next });
  };

  const selectEnd = (picked) => {
    const next = Number(picked);
    if (next < start) return;
    onChange({ rowsSelectionEnd: next >= total ? null : next });
  };

  return (
    <Group grow gap="xs">
      <Select
        size="xs"
        label={i18n.t('start-line')}
        data={options}
        value={String(start)}
        onChange={selectStart}
        disabled={disabled}
        allowDeselect={false}
        comboboxProps={{ withinPortal: true }}
      />
      <Select
        size="xs"
        label={i18n.t('end-line')}
        data={options}
        value={String(end)}
        onChange={selectEnd}
        disabled={disabled}
        allowDeselect={false}
        comboboxProps={{ withinPortal: true }}
      />
    </Group>
  );
}
